import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { ApiResponse, Game, GameStatus, Player } from '../types/models';

// Rejection value for failed API calls. `status` is undefined for network
// failures (no response). `error` mirrors `message` because callers read both.
export class ApiError extends Error {
  status?: number;
  error: string;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.error = message;
  }
}

// Create axios instance with default config
const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important for CSRF token handling
});

// Request interceptor to add player ID header
api.interceptors.request.use((config) => {
  // Extract game key from URL patterns like /games/{key} or /games/{key}/...
  const gameKey = config.url?.match(/\/games\/([^\/]+)/)?.[1];
  if (gameKey) {
    const playerId = localStorage.getItem(`game_${gameKey}_player_id`);
    if (playerId) {
      config.headers['X-Player-Id'] = playerId;
    }
  }
  return config;
});

// Transform game data to ensure status is properly handled
const transformGameResponse = (game: any): Game => {
  if (!game) return game;
  
  // Map numeric status to enum string
  let status = game.status;
  if (typeof status === 'number') {
    // Map 0 -> 'lobby', 1 -> 'in_progress', 2 -> 'completed'
    const statusMap = {
      0: GameStatus.LOBBY,
      1: GameStatus.IN_PROGRESS,
      2: GameStatus.COMPLETED
    };
    status = statusMap[status];
  } else if (typeof status === 'string') {
    status = status.toLowerCase();
  }
  
  if (!Object.values(GameStatus).includes(status)) {
    console.warn(`Invalid game status received: ${status}`);
  }

  const players = game.players || [];
  
  const transformedGame = {
    ...game,
    status: status as GameStatus,
    players
  };
  
  return transformedGame;
};

// Response interceptor to standardize response format
api.interceptors.response.use(
  (response: AxiosResponse) => {
    // Only transform responses that contain game data
    const url = response.config.url || '';
    const isGameEndpoint = url.includes('/games'); // Check if it's a game-related endpoint
    const dataIsGameOrGameArray = response.data && (response.data.key || (Array.isArray(response.data) && response.data[0]?.key)); // Check if data looks like a game or array of games
    
    if (isGameEndpoint && dataIsGameOrGameArray) {
      if (Array.isArray(response.data)) {
        response.data = response.data.map(transformGameResponse);
      } else {
        response.data = transformGameResponse(response.data);
      }
    }
    
    return {
      data: response.data,
      error: null,
    };
  },
  (error) => {
    // Log errors in development only
    if (process.env.NODE_ENV === 'development') {
      console.error('API Error:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
    }
    
    const message =
      error.response?.data?.errors?.[0] ||
      error.response?.data?.error ||
      (error.response
        ? 'An unexpected error occurred'
        : 'Could not reach the server. Please check your connection.');
    return Promise.reject(new ApiError(message, error.response?.status));
  }
);

interface CreateStackEntryRequest {
  content: string;
  contentOpaque?: string;
  type: 'text' | 'drawing';
}

interface CreateGameRequest {
  hostId?: string;
}

// Type-safe API methods
export const apiClient = {
  // Game endpoints
  async createGame(data?: CreateGameRequest): Promise<ApiResponse<Game>> {
    return api.post('/games', data ? { game: data } : undefined);
  },

  async getGameByKey(gameKey: string): Promise<ApiResponse<Game>> {
    return api.get(`/games/${gameKey}`);
  },

  async startGame(gameKey: string): Promise<ApiResponse<Game>> {
    return api.post(`/games/${gameKey}/start`);
  },

  // Player endpoints
  async getPlayer(gameKey: string, playerId: string): Promise<ApiResponse<Player>> {
    return api.get(`/games/${gameKey}/players/${playerId}`);
  },

  async createPlayer(gameKey: string, data: {
    name: string;
    avatarUrl?: string | null;
    avatarThumbUrl?: string | null;
    isHost?: boolean;
    handwritingFont?: string;
  }): Promise<ApiResponse<Player>> {
    return api.post(`/games/${gameKey}/players`, {
      name: data.name,
      avatar_url: data.avatarUrl,
      avatar_thumb_url: data.avatarThumbUrl,
      is_host: data.isHost,
      handwriting_font: data.handwritingFont
    });
  },

  async removePlayer(gameKey: string, playerId: number): Promise<ApiResponse<void>> {
    return api.delete(`/games/${gameKey}/players/${playerId}`);
  },

  async updatePlayer(gameKey: string, playerId: string, data: {
    handwritingFont?: string;
    avatarUrl?: string | null;
    avatarThumbUrl?: string | null;
  }): Promise<ApiResponse<Player>> {
    const payload: Record<string, string | boolean | null | undefined> = {
      handwriting_font: data.handwritingFont,
      avatar_url: data.avatarUrl,
      avatar_thumb_url: data.avatarThumbUrl
    };
    return api.patch(`/games/${gameKey}/players/${playerId}`, payload);
  },

  async getAvatarHistory(gameKey: string, playerId: string): Promise<ApiResponse<{ avatars: Array<{
    id: string;
    avatarUrl: string;
    avatarThumbUrl?: string | null;
    createdAt: string;
    source?: string;
  }> }>> {
    return api.get(`/games/${gameKey}/players/${playerId}/avatar_history`);
  },

  async deleteAvatarHistory(gameKey: string, playerId: string, historyId: string): Promise<ApiResponse<{ success: boolean }>> {
    return api.delete(`/games/${gameKey}/players/${playerId}/avatar_history/${historyId}`);
  },

  // Stack endpoints
  async getStacks(gameKey: string): Promise<ApiResponse<Stack[]>> {
    return api.get(`/games/${gameKey}/stacks`);
  },

  async createStack(gameKey: string, data: {
    prompt: string;
  }): Promise<ApiResponse<Stack>> {
    return api.post(`/games/${gameKey}/stacks`, data);
  },

  async createStackEntry(gameKey: string, stackId: string, data: CreateStackEntryRequest): Promise<ApiResponse<Stack>> {
    return api.post(`/games/${gameKey}/stacks/${stackId}/entries`, data);
  },

  // Queue management
  async pickupStack(gameKey: string, stackId: string): Promise<ApiResponse<{ message: string }>> {
    return api.patch(`/games/${gameKey}/stacks/${stackId}/pickup`);
  },

  // Push notifications
  async getVapidPublicKey(): Promise<ApiResponse<{ public_key: string }>> {
    return api.get('/push/vapid_public_key');
  },

  async savePushSubscription(gameKey: string, subscription: {
    endpoint: string;
    keys: { p256dh: string; auth: string };
  }): Promise<ApiResponse<{ id: string; status: string }>> {
    return api.post(`/games/${gameKey}/push_subscriptions`, { subscription });
  },

  async deletePushSubscription(gameKey: string, endpoint: string): Promise<ApiResponse<{ status: string }>> {
    return api.delete(`/games/${gameKey}/push_subscriptions`, { params: { endpoint } });
  },

  // Drawing upload
  async uploadDrawing(blob: Blob): Promise<ApiResponse<{ url: string }>> {
    const formData = new FormData();
    formData.append('file', blob);
    const response = await api.post('/uploads', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });


    return response;
  },
};

export default apiClient; 
